import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography, Divider, Collapse } from '@mui/material';
import { ChevronDown, ChevronRight, ChevronLeft, Circle } from 'lucide-react';
import { MODULE_CONFIGS, type ModuleId } from '../theme/moduleColors';

export interface NavSection { title: string; items: NavItem[]; collapsed?: boolean; }
export interface NavItem { path: string; label: string; icon?: React.ReactElement; }
export type ModuleNavigation = NavSection[];

interface ModuleSidebarProps {
  moduleId: ModuleId;
  navigation: ModuleNavigation;
  onCollapse?: () => void;
  isRtl?: boolean;
}

// Slug English label → translation-key suffix.
// "Manager Dashboard" → "manager_dashboard". Stable across locales because
// navigation files always ship English source labels (BRD §11.3 doesn't apply
// to engineering source — only to rendered UI).
const slug = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');

const ModuleSidebar: React.FC<ModuleSidebarProps> = ({ moduleId, navigation, onCollapse, isRtl = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const config = MODULE_CONFIGS[moduleId];
  const [openSections, setOpenSections] = React.useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    navigation.forEach((s) => { init[s.title] = !s.collapsed; });
    return init;
  });
  const toggleSection = (title: string) => { setOpenSections((prev) => ({ ...prev, [title]: !prev[title] })); };
  const isActive = (path: string) => location.pathname === path;

  // Bilingual sidebar — falls back to the English source string if a key is missing.
  const moduleLabel = t(`modules.${moduleId}.label`, { defaultValue: config.label });
  const moduleDescription = t(`modules.${moduleId}.description`, { defaultValue: config.description });

  // Collapse chevron points "outward" from the page-edge — left in LTR, right in RTL.
  const CollapseIcon = isRtl ? ChevronRight : ChevronLeft;

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      <Box sx={{ px: 2, py: 1, mb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ color: config.accent, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{moduleLabel}</Typography>
          <Typography variant="caption" sx={{ color: '#4A5568' }}>{moduleDescription}</Typography>
        </Box>
        {onCollapse && (
          <Tooltip title={isRtl ? 'إخفاء القائمة' : 'Collapse sidebar'} placement="bottom">
            <IconButton
              size="small"
              onClick={onCollapse}
              aria-label={isRtl ? 'إخفاء القائمة الجانبية' : 'Collapse sidebar'}
              sx={{ color: 'text.secondary', flexShrink: 0, '&:hover': { color: 'primary.main', bgcolor: 'rgba(11, 37, 69, 0.04)' } }}
            >
              <CollapseIcon size={16} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Divider sx={{ borderColor: '#E3E7EC' }} />
      {navigation.map((section) => {
        const sectionLabel = t(`sidebar.section.${slug(section.title)}`, { defaultValue: section.title });
        return (
          <Box key={section.title}>
            <ListItem disablePadding sx={{ px: 1 }} onClick={() => toggleSection(section.title)}>
              <ListItemButton sx={{ borderRadius: 1, py: 0.5 }}>
                <ListItemText primary={sectionLabel} primaryTypographyProps={{ fontSize: '0.75rem', fontWeight: 600, color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.03em' }} />
                {openSections[section.title] ? <ChevronDown size={14} color="#506070" /> : <ChevronRight size={14} color="#506070" />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openSections[section.title]} timeout="auto" unmountOnExit>
              <List disablePadding>
                {section.items.map((item) => {
                  const itemLabel = t(`sidebar.item.${slug(item.label)}`, { defaultValue: item.label });
                  return (
                    <ListItem key={item.path} disablePadding sx={{ px: 1 }}>
                      <ListItemButton onClick={() => navigate(item.path)} selected={isActive(item.path)} sx={{
                        borderRadius: 1, mb: 0.25, py: 0.5, pl: 3,
                        '&.Mui-selected': { bgcolor: `${config.accent}14`, '&:hover': { bgcolor: `${config.accent}20` }, '& .MuiListItemIcon-root': { color: config.accent }, '& .MuiListItemText-primary': { color: config.accent, fontWeight: 600 } },
                      }}>
                        <ListItemIcon sx={{ minWidth: 28, color: isActive(item.path) ? config.accent : '#506070' }}>
                          {item.icon || <Circle size={6} />}
                        </ListItemIcon>
                        <ListItemText primary={itemLabel} primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: isActive(item.path) ? 600 : 400 }} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

export default ModuleSidebar;
